/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, IModalComponentProps } from './form-control';
import DynamicCheckBox from './form-control/form-builder/form-builder-element/checkbox';

interface IFormValues {
    journal_type_data: {
        name: string;
        is_payment: boolean;
    };
}

const journalTypeValidationSchema = Yup.object().shape({
    journal_type_data: Yup.object().shape({
        name: Yup.string()
            .required('Name is required')
            .max(100, 'Name must be at most 100 characters'),
        is_payment: Yup.boolean()
    })
});

export const JournalTypeAddModal: React.FC<IModalComponentProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(journalTypeValidationSchema),
        defaultValues: {
            journal_type_data: {
                name: '',
                is_payment: false
            }
        }
    });

    const handleModalClose = () => {
        reset({ 
            journal_type_data: { 
                name: '', 
                is_payment: false 
            } 
        });
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave(values?.journal_type_data as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='journalTypeAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t("accounting.journalType.addModal.title", "Create New Journal Type")}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='journalTypeAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("accounting.journalType.form.name_label", "Name")}
                                    name='name'
                                    fieldArrayName='journal_type_data'
                                    placeholder={t("accounting.journalType.form.name_placeholder", "Enter journal type name")}
                                    required
                                />
                            </Grid>
                            <Grid item sm={12}>
                                <DynamicCheckBox
                                    formControl={control}
                                    label={t("accounting.journalType.form.is_payment_label", "Is Payment")}
                                    name='is_payment'
                                    fieldArrayName='journal_type_data'
                                    title={t("accounting.journalType.form.is_payment_label", "Is Payment")}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className='journalTypeAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='journalTypeAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t("common.cancel", "Cancel")}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>
                                {t("common.save", "Save")}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
